import { Card, Box, Slider, Stack, Typography } from '@mui/material'
import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { filterDefaultValues } from 'components/configured-service/CreateDraftConfiguredServiceDialog/filterSettings'
import useRadiusUnit from 'components/configured-service/Schedule/hooks/useRadiusUnit'
import OverCapacityNotice from 'components/configured-service/Schedule/OverCapacityNotice'
import T from 'T'

interface MapRadiusControlProps {
  truncated?: boolean
  totalMatched?: number
  cap?: number
  distanceUnit?: string
  radiusMinMeters?: number
  radiusMaxMeters?: number
  radiusStepMeters?: number
}

/**
 * On-map radius control. Bound to the same `radius` form field as the filters panel slider, so the
 * two stay in sync — this puts the lever next to the markers it thins out, and repeats the cap
 * count where the user sees the crowding.
 */
const MapRadiusControl: React.FC<MapRadiusControlProps> = ({
  truncated = false,
  totalMatched = 0,
  cap = 0,
  distanceUnit,
  radiusMinMeters,
  radiusMaxMeters,
  radiusStepMeters,
}) => {
  const { control } = useFormContext()
  // Passed through undefined so useRadiusUnit's own defaults apply — it is side-effecting (it seeds
  // radiusUnit and snaps `radius` to its slider grid) and this is the second instance on screen, so
  // both must be configured identically or they fight over the same form fields.
  const { unitLabel, sliderConfig, displayUnitValue, onSliderChange, onSliderCommit } = useRadiusUnit({
    distanceUnitProp: distanceUnit,
    radiusMinMeters,
    radiusMaxMeters,
    radiusStepMeters,
  })

  return (
    <Card sx={{ width: 300, px: 2, py: 1.5, m: 1 }} variant="outlined">
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Typography id="map-radius-slider" variant="h6">{`${T.RADIUS} (${unitLabel})`}</Typography>
        <Typography color="text.secondary" variant="body2">
          {`${displayUnitValue} ${unitLabel}`}
        </Typography>
      </Stack>
      <Controller
        control={control}
        defaultValue={filterDefaultValues.radius}
        name="radius"
        render={() => {
          const handleSliderChange = (event: Event, val: number | number[]) => onSliderChange(event, val)
          const handleSliderChangeCommitted = (event: Event | React.SyntheticEvent, val: number | number[]) => onSliderCommit(event, val)
          return (
            <Slider
              aria-labelledby="map-radius-slider"
              max={sliderConfig.max}
              min={sliderConfig.min}
              shiftStep={sliderConfig.step}
              step={sliderConfig.step}
              value={displayUnitValue}
              valueLabelDisplay="auto"
              onChange={handleSliderChange}
              onChangeCommitted={handleSliderChangeCommitted}
            />
          )
        }}
      />
      {truncated && (
        <Box mt={0.5}>
          <OverCapacityNotice cap={cap} totalMatched={totalMatched} truncated={truncated} variant="caption" />
        </Box>
      )}
    </Card>
  )
}

export default MapRadiusControl
